import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  validateFileSize,
  validateMimeType,
  validateFileExtension,
  validateMagicBytes,
} from './validations/fileValidation';
import { UploadButtons } from './components/UploadButtons';
import { ScreenLoading } from './components/ScreenLoading';
import { ScreenSuccess } from './components/ScreenSuccess';
import { ScreenError } from './components/ScreenError';
import { ScreenExpired } from './components/ScreenExpired';
import { ScreenPartial } from './components/ScreenPartial';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

type Step = 'loading' | 'ready' | 'partial' | 'uploading' | 'done' | 'error' | 'expired';

export interface PagePreview {
  url: string;
  mimeType: string;
}

interface State {
  step: Step;
  documentLabel: string;
  errorMessage: string;
  pages: PagePreview[];
}

type Action =
  | { type: 'link/validated'; documentLabel: string }
  | { type: 'link/expired' }
  | { type: 'page/added'; page: PagePreview }
  | { type: 'upload/start' }
  | { type: 'upload/success' }
  | { type: 'upload/fail'; message: string };

const initialState: State = {
  step: 'loading',
  documentLabel: 'Document',
  errorMessage: '',
  pages: [],
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'link/validated':
      return { ...state, step: 'ready', documentLabel: action.documentLabel };
    case 'link/expired':
      return { ...state, step: 'expired' };
    case 'page/added':
      return { ...state, step: 'partial', pages: [...state.pages, action.page] };
    case 'upload/start':
      return { ...state, step: 'uploading', errorMessage: '' };
    case 'upload/success':
      return { ...state, step: 'done' };
    case 'upload/fail':
      return { ...state, step: 'error', errorMessage: action.message };
    default:
      return state;
  }
}

function getParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

export function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const token = useMemo(() => getParam('token'), []);
  const pendingFiles = useRef<File[]>([]);

  useEffect(() => {
    if (!token) {
      dispatch({ type: 'link/expired' });
      return;
    }
    fetch(`${API_BASE_URL}/upload-link/validate/${token}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        dispatch({
          type: 'link/validated',
          documentLabel: data?.data?.documentLabel ?? 'Document',
        });
      })
      .catch(() => dispatch({ type: 'link/expired' }));
  }, [token]);

  const validateFile = useCallback(async (file: File): Promise<string | null> => {
    for (const check of [
      () => validateFileSize(file.size),
      () => validateMimeType(file.type),
      () => validateFileExtension(file.name, file.type),
    ]) {
      const result = check();
      if (!result.valid) return result.error!;
    }
    try {
      const buffer = await file.slice(0, 12).arrayBuffer();
      const magicCheck = validateMagicBytes(Array.from(new Uint8Array(buffer)), file.type);
      if (!magicCheck.valid) return magicCheck.error!;
    } catch {
      /* skip */
    }
    return null;
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      const error = await validateFile(file);
      if (error) {
        dispatch({ type: 'upload/fail', message: error });
        return;
      }
      pendingFiles.current.push(file);
      dispatch({ type: 'page/added', page: { url: URL.createObjectURL(file), mimeType: file.type } });
    },
    [validateFile]
  );

  const handleFinalize = useCallback(async () => {
    dispatch({ type: 'upload/start' });
    try {
      for (const file of pendingFiles.current) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE_URL}/upload-link/upload/${token}`, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          dispatch({
            type: 'upload/fail',
            message: body?.error?.message || "Une erreur est survenue lors de l'envoi.",
          });
          return;
        }
      }

      const res = await fetch(`${API_BASE_URL}/upload-link/finalize/${token}`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        dispatch({
          type: 'upload/fail',
          message: body?.error?.message || "Une erreur est survenue lors de la finalisation.",
        });
        return;
      }
      dispatch({ type: 'upload/success' });
    } catch {
      dispatch({
        type: 'upload/fail',
        message: 'Impossible de contacter le serveur. Vérifiez votre connexion internet.',
      });
    }
  }, [token]);

  const { step, documentLabel, errorMessage, pages } = state;

  return (
    <div className="flex min-h-dvh flex-col bg-gray-50 font-sans">
      <header className="flex justify-center bg-white px-5 pb-4 pt-8">
        <img
          src={`${process.env.PUBLIC_URL}/logo-transitions-pro.png`}
          alt="Transitions Pro"
          className="h-16 w-auto"
        />
      </header>

      <main className="flex flex-1 flex-col items-center px-6 pb-10 pt-6">
        {step === 'loading' && <ScreenLoading text="Chargement..." />}

        {step === 'ready' && (
          <div className="w-full max-w-[400px]">
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-xl font-semibold text-brand-700">
                Importer votre {documentLabel.toLowerCase()}
              </h1>
              <p className="text-[15px] leading-relaxed text-gray-500">
                Sélectionner une méthode pour ajouter
                <br />
                le document depuis votre téléphone
              </p>
            </div>
            <UploadButtons onFile={handleFile} />
            <p className="mt-6 text-center text-xs text-gray-400">
              Formats acceptés : PDF, JPEG, PNG — 5 Mo max
            </p>
          </div>
        )}

        {step === 'partial' && (
          <ScreenPartial
            pages={pages}
            onAddPage={handleFile}
            onFinalize={handleFinalize}
          />
        )}
        {step === 'uploading' && <ScreenLoading text="Envoi en cours..." />}
        {step === 'done' && <ScreenSuccess />}
        {step === 'error' && (
          <ScreenError message={errorMessage}>
            <UploadButtons onFile={handleFile} />
          </ScreenError>
        )}
        {step === 'expired' && <ScreenExpired />}
      </main>

      <footer className="h-2 bg-brand-800" />
    </div>
  );
}
